import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import {
  listAccountability, updateAccountability, accountabilityWeekBuckets,
  accountabilityCoachStats, accountabilityReports, backfillAccountability,
  listRoster, updateAccountabilityCompliance, listAccountabilityNotStarted, updateLead, getMerchStock, merchKey,
  getAccountability,
} from "@/lib/api";
import { NeedsAttention, CoachScoreboard, useLowScoreCoaches } from "@/components/AccountabilityCoaches";
import { DatePicker } from "@/components/DatePicker";
import { Link, useSearchParams } from "react-router-dom";
import { todayLocal } from "@/lib/periods";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/SearchableSelect";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { atLeast } from "@/lib/roles";
import { formatGuarantee } from "@/lib/guarantee";
import {
  Trophy, Users2, Sparkles, AlertTriangle, Droplet, Star,
  MessagesSquare, RefreshCw, ChevronDown, ChevronUp, Filter,
  CheckCircle2, Loader2, XCircle, ChevronRight, Columns3, EyeOff, Eye, Search, Zap,
} from "lucide-react";

const STATUS_CHOICES = ["Pending", "Done", "Missed", "NA"];
const CONSULT_CHOICES = ["Pending", "Done", "Scheduled", "Missed", "NA"];
const TRIL_CHOICES = ["Pending", "Yes", "No", "NA"];
const PROG_STATUS = ["Active", "Inactive", "Dropout", "Extension"];
const CHAT_STATUS = ["Active", "Inactive", "Dropout"];
const GOAL_CHOICES = ["", "Recomp", "Weight Loss", "Weight Gain"];
const FF_STATUS = ["Pending", "Filled", "NA"];
const FIT3D_STATUS = ["Pending", "Done", "Missed", "NA"];

const badge = (val, colors = {}) => {
  const v = (val || "").toLowerCase();
  const map = {
    done: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
    filled: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
    yes: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
    active: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
    missed: "border-rose-500/30 bg-rose-500/10 text-rose-300",
    no: "border-rose-500/30 bg-rose-500/10 text-rose-300",
    dropout: "border-rose-500/30 bg-rose-500/10 text-rose-300",
    pending: "border-amber-500/30 bg-amber-500/10 text-amber-300",
    scheduled: "border-amber-500/30 bg-amber-500/10 text-amber-300",
    na: "border-zinc-600/50 bg-zinc-800/40 text-zinc-400",
    extension: "border-blue-500/30 bg-blue-500/10 text-blue-300",
    inactive: "border-zinc-600/50 bg-zinc-800/40 text-zinc-400",
    ...colors,
  };
  return map[v] || "border-zinc-600/50 bg-zinc-800/40 text-zinc-400";
};

// Post-sale compliance items live in a separate dict + dedicated PATCH endpoint (server
// restricts edits to the assigned coach / member manager / admins via 403).
const COMPLIANCE_ITEMS = [
  { key: "wa_welcome_tee_card", label: "WA welcome + tee card" },
  { key: "tshirt_billing_done_compliance", label: "T-shirt billing" },
  { key: "trainerise_welcome_video", label: "Trainerise + video" },
  { key: "mid_final_week_booked", label: "Mid+final consult booked" },
  { key: "billing_done", label: "Billing done" },
  { key: "blood_test", label: "Blood test" },
  { key: "tshirt_given", label: "T-shirt given" },
  { key: "handwritten_card", label: "Handwritten card" },
  { key: "tshirt_billing_done", label: "Tshirt + billing F/U" },
];

// Column groups define the Excel-style grid. `type` picks the cell editor:
//   text | num | date | sel (with choices) | bool (Yes/No select) | ro (read-only)
//   person (pick from the gym's Team Roster: Coach / Nutrition coach, CR-0007) | rodate (date, read-only)
//   rotext (read-only text, `fmt(value)` formats it; shown on the phone sheet too)
// `comp` marks compliance-dict columns which go through the compliance endpoint instead.
const COLUMN_GROUPS = [
  { group: "Client", cols: [
    { key: "__client", label: "Client", type: "ro", sticky: true, width: 200 },
  ]},
  { group: "Basics", cols: [
    { key: "coach", label: "Coach", type: "person", roster: "coach", width: 140 },
    { key: "nutrition_coach", label: "Nutrition coach", type: "person", roster: "nutrition", width: 140 },
    { key: "program", label: "Programme", type: "text", width: 110 },
    { key: "start_date", label: "Start", type: "rodate", width: 80 },
    { key: "end_date", label: "End", type: "rodate", width: 80 },
    { key: "goal", label: "Goal", type: "sel", choices: GOAL_CHOICES, width: 110 },
    { key: "programme_status", label: "Prog status", type: "sel", choices: PROG_STATUS, width: 105 },
    { key: "chat_status", label: "Chat", type: "sel", choices: CHAT_STATUS, width: 100 },
  ]},
  { group: "Weights", cols: [
    { key: "w0_weight", label: "W0 kg", type: "num", width: 75 },
    { key: "promised_end_result", label: "Promised", type: "num", width: 85 },
    // Result promised at the consult (CR-0042); edited on the consult, not here
    { key: "guarantee", label: "Guarantee", type: "rotext", fmt: formatGuarantee, width: 150 },
    { key: "current_weight", label: "Curr wt", type: "num", width: 80 },
    { key: "current_bf_pct", label: "BF %", type: "num", width: 70 },
    { key: "current_measurement", label: "Meas", type: "num", width: 75 },
  ]},
  { group: "Blood tests", cols: [
    { key: "blood_test_required", label: "BT req", type: "bool", width: 75 },
    { key: "bt1_scheduled_date", label: "BT1 date", type: "date", width: 135 },
    { key: "bt1_done", label: "BT1", type: "sel", choices: STATUS_CHOICES, width: 95 },
    { key: "bt2_scheduled_date", label: "BT2 date", type: "date", width: 135 },
    { key: "bt2_done", label: "BT2", type: "sel", choices: STATUS_CHOICES, width: 95 },
  ]},
  { group: "Compliance & consults", cols: [
    { key: "psc_signed", label: "PSC signed", type: "sel", choices: TRIL_CHOICES, width: 100 },
    { key: "bio_metric_access", label: "Biometric", type: "sel", choices: TRIL_CHOICES, width: 95 },
    { key: "nutrition_consult_booked", label: "Nutri consult", type: "sel", choices: CONSULT_CHOICES, width: 110 },
    { key: "halfway_consult_booked", label: "Halfway", type: "sel", choices: CONSULT_CHOICES, width: 95 },
    { key: "end_consult_booked", label: "End consult", type: "sel", choices: CONSULT_CHOICES, width: 100 },
    { key: "call_after_wk2", label: "Call wk2", type: "sel", choices: STATUS_CHOICES, width: 90 },
    { key: "call_after_wk4", label: "Call wk4", type: "sel", choices: STATUS_CHOICES, width: 90 },
  ]},
  { group: "Weekly touchpoints", cols: [
    { key: "wk1_reachout", label: "Wk1 call", type: "sel", choices: STATUS_CHOICES, width: 90 },
    { key: "wk2_video", label: "Wk2 video", type: "sel", choices: STATUS_CHOICES, width: 95 },
    { key: "wk3_shoutout", label: "Wk3 shout", type: "sel", choices: STATUS_CHOICES, width: 90 },
    { key: "wk4_card", label: "Wk4 card", type: "sel", choices: STATUS_CHOICES, width: 90 },
    { key: "wk5_video", label: "Wk5 video", type: "sel", choices: STATUS_CHOICES, width: 95 },
  ]},
  { group: "Mid-week", cols: [
    { key: "midweek_date", label: "Date", type: "date", width: 135 },
    { key: "midweek_meeting_done", label: "Done", type: "sel", choices: STATUS_CHOICES, width: 90 },
    { key: "midweek_nutrition_confirm", label: "Nutri confirm", type: "text", width: 120 },
    { key: "midweek_reachouts_status", label: "Reachouts", type: "text", width: 110 },
    { key: "midweek_feedback", label: "Feedback", type: "text", width: 150 },
  ]},
  { group: "Final-week", cols: [
    { key: "finalweek_date", label: "Date", type: "date", width: 135 },
    { key: "finalweek_meeting_done", label: "Done", type: "sel", choices: STATUS_CHOICES, width: 90 },
    { key: "finalweek_verify_reachouts", label: "Verify reach", type: "text", width: 110 },
    { key: "finalweek_video_check", label: "Video check", type: "text", width: 110 },
    { key: "finalweek_feedback", label: "Feedback", type: "text", width: 150 },
  ]},
  { group: "Feedback forms", cols: [
    { key: "feedback_form_1_status", label: "FF1 status", type: "sel", choices: FF_STATUS, width: 100 },
    { key: "feedback_form_1_score", label: "FF1 score", type: "num", width: 90 },
    { key: "feedback_form_2_status", label: "FF2 status", type: "sel", choices: FF_STATUS, width: 100 },
    { key: "feedback_form_2_score", label: "FF2 score", type: "num", width: 90 },
  ]},
  { group: "Outcomes", cols: [
    { key: "finished", label: "Finished", type: "bool", width: 85 },
    { key: "extension_given", label: "Extension", type: "bool", width: 90 },
    { key: "results_achieved", label: "Results", type: "sel", choices: TRIL_CHOICES, width: 90 },
    { key: "ig_post", label: "IG post", type: "sel", choices: TRIL_CHOICES, width: 85 },
    { key: "google_review", label: "Google rev", type: "sel", choices: [...TRIL_CHOICES, "Missed"], width: 100 },
    { key: "__score", label: "Score", type: "ro", width: 80 },
    { key: "__fit3d", label: "Checks", type: "ro", width: 70 },
  ]},
  { group: "Post-sale compliance", cols: COMPLIANCE_ITEMS.map((c) => ({
    key: `comp:${c.key}`, label: c.label, type: "check", comp: c.key, width: 70,
  }))},
];

// Cells filled in from data (backend routes/accountability_auto.py) are shown, not edited.
const autoText = (v) => (v === true ? "Yes" : v === false ? "No" : v == null || v === "" ? "—" : String(v));

// Blood test detail cells: shown as "—" when the member didn't take blood tests (CR-0024)
const BT_DETAIL_KEYS = new Set(["bt1_scheduled_date", "bt1_done", "bt2_scheduled_date", "bt2_done"]);
const shortDate = (d) => (d ? new Date(`${String(d).slice(0, 10)}T00:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : "—");
// "2026-12" -> "December 2026"
const monthLabel = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString(undefined, { month: "long", year: "numeric" });
// Gyms that don't offer blood tests (Settings → Accountability) don't see the Blood tests group.
const groupsFor = (noBloodTests) => (noBloodTests
  ? COLUMN_GROUPS.filter((g) => g.group !== "Blood tests")
    .map((g) => ({ ...g, cols: g.cols.filter((c) => c.comp !== "blood_test") }))
  : COLUMN_GROUPS);

// Flatten once — used by the header and every row render.
const FLAT_COLS = COLUMN_GROUPS.flatMap((g) => g.cols);
const TOTAL_COLS = FLAT_COLS.length;

export default function Accountability() {
  const { user, activeFacility } = useAuth();
  const [rows, setRows] = useState([]);
  const [buckets, setBuckets] = useState([]);
  const [stats, setStats] = useState([]);
  const [reports, setReports] = useState({ feedback_gaps: [], google_review_gaps: [], low_score_coaches: [], bt_not_scheduled: [] });
  const [coaches, setCoaches] = useState([]);
  const [filters, setFilters] = useState({ coach: "all", week_bucket: "all", status: "all" });
  // On phones, open straight onto the client list; the summaries are a tap away.
  const wide = typeof window === "undefined" || window.innerWidth >= 768;
  const [showLeaderboard, setShowLeaderboard] = useState(wide);
  const [showReports, setShowReports] = useState(wide);
  const [showFilters, setShowFilters] = useState(wide);
  // Column visibility — persisted per facility so a coach's lean view and a team lead's
  // full view don't stomp on each other when different users sign in from the same browser.
  const [hiddenCols, setHiddenCols] = useState(() => loadHiddenCols(activeFacility?.id));
  useEffect(() => { setHiddenCols(loadHiddenCols(activeFacility?.id)); }, [activeFacility?.id]);
  useEffect(() => { saveHiddenCols(activeFacility?.id, hiddenCols); }, [hiddenCols, activeFacility?.id]);

  const load = () => {
    const params = {};
    if (filters.coach && filters.coach !== "all") params.coach = filters.coach;
    if (filters.week_bucket && filters.week_bucket !== "all") params.week_bucket = filters.week_bucket;
    if (filters.status && filters.status !== "all") params.status = filters.status;
    listAccountability(params).then(setRows).catch(() => setRows([]));
    accountabilityWeekBuckets().then(setBuckets).catch(() => setBuckets([]));
    accountabilityCoachStats().then(setStats).catch(() => setStats([]));
    accountabilityReports().then(setReports).catch(() => {});
  };

  const lowScore = useLowScoreCoaches();
  // A client opened from "Needs attention" (the same sheet phones use for every client).
  const [openRowId, setOpenRowId] = useState(null);
  const [attentionKey, setAttentionKey] = useState(0);
  const openClient = async (id) => {
    if (!rows.some((r) => r.id === id)) {
      try {
        const r = await getAccountability(id);
        setRows((rs) => [...(rs || []), r]);
      } catch {
        return toast.error("Couldn't open that client");
      }
    }
    setOpenRowId(id);
  };
  const openRow = rows.find((r) => r.id === openRowId);

  const [nutritionCoaches, setNutritionCoaches] = useState([]);
  const [searchParams] = useSearchParams();
  const [q, setQ] = useState(() => searchParams.get("q") || "");   // notices link here with ?q=<client>
  // "Ends in" month filter (CR-0061): uses the End column (stored end date, or start + programme length)
  const [endMonth, setEndMonth] = useState("all");
  const endMonths = useMemo(() => {
    const ms = new Set((rows || []).map((r) => String(r.end_date || "").slice(0, 7)).filter((m) => /^\d{4}-\d{2}$/.test(m)));
    if (endMonth !== "all") ms.add(endMonth);
    return [...ms].sort();
  }, [rows, endMonth]);
  const noBloodTests = activeFacility?.offers_blood_tests === false;
  useEffect(() => {
    listRoster("accountability").then(setCoaches).catch(() => setCoaches([]));
    listRoster("nutrition_consult").then(setNutritionCoaches).catch(() => setNutritionCoaches([]));
  }, [activeFacility?.id]);
  // Names for the Coach / Nutrition coach dropdowns (CR-0007)
  const people = useMemo(() => ({
    coach: [...new Set(coaches.map((c) => c.name).filter(Boolean))],
    nutrition: [...new Set(nutritionCoaches.map((c) => c.name).filter(Boolean))],
  }), [coaches, nutritionCoaches]);
  // One search for the grid and the phone list (CR-0031): client, phone or coach
  const shownRows = useMemo(() => {
    const t = q.trim().toLowerCase();
    return (rows || []).filter((r) => (endMonth === "all" || String(r.end_date || "").slice(0, 7) === endMonth)
      && (!t || `${r.client_name} ${r.phone || ""} ${r.coach || ""} ${r.nutrition_coach || ""}`.toLowerCase().includes(t)));
  }, [rows, q, endMonth]);
  // T-shirt sizes in stock at this gym. When T-shirts are stock-tracked, ticking "T-shirt
  // given" asks for the size (or lets staff mark it not given because it's out of stock).
  const [tshirtStock, setTshirtStock] = useState([]);
  const [tshirtAsk, setTshirtAsk] = useState(null); // { rowId, name, resolve }
  const loadTshirtStock = () => getMerchStock()
    .then((r) => setTshirtStock((r.stock || []).filter((x) => merchKey(x.item) === "tshirt")))
    .catch(() => setTshirtStock([]));
  useEffect(() => { loadTshirtStock(); }, [activeFacility?.id]);
  useEffect(() => { load(); }, [filters]);

  // Optimistic per-cell save — the whole point of the grid is one-click edits with no
  // drawer round-trip. On failure we roll the cell back and toast the server error.
  // Returns true/false so the row-level save indicator reflects the real outcome.
  const patchCell = async (rowId, key, value) => {
    const prev = rows.find((r) => r.id === rowId)?.[key];
    // Nutri consult = Done starts the programme (Anish, 23 Oct 2026): ask which day it was done.
    if (key === "nutrition_consult_booked" && value === "Done" && String(prev || "").toLowerCase() !== "done") {
      const row = rows.find((r) => r.id === rowId);
      return new Promise((resolve) => setNutriAsk({ rowId, name: row?.client_name, resolve }));
    }
    if (key === "nutrition_consult_booked") return sendNutri(rowId, { nutrition_consult_booked: value });
    setRows((rs) => (rs || []).map((r) => (r.id === rowId ? { ...r, [key]: value } : r)));
    try {
      await updateAccountability(rowId, { [key]: value });
      return true;
    } catch (e) {
      setRows((rs) => (rs || []).map((r) => (r.id === rowId ? { ...r, [key]: prev } : r)));
      toast.error(e?.response?.data?.detail || "Save failed");
      return false;
    }
  };

  // Nutrition consult changes come back with the day (and may be refused: only the client's people may change it).
  const [nutriAsk, setNutriAsk] = useState(null); // { rowId, name, resolve }
  const sendNutri = async (rowId, payload) => {
    try {
      const res = await updateAccountability(rowId, payload);
      setRows((rs) => (rs || []).map((r) => (r.id === rowId ? { ...r, ...res, compliance: r.compliance } : r)));
      if (String(payload.nutrition_consult_booked || "").toLowerCase() === "done") {
        toast.success(`Nutrition consult done. ${res.client_name ? `${res.client_name}'s programme` : "The programme"} started ${res.nutrition_consult_done_on}.`);
      }
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
      return false;
    }
  };

  // Compliance cells hit a different endpoint (server-enforced permissions: only the
  // assigned coach / member manager / admins can tick them). Revert on 403.
  const patchCompliance = async (rowId, compKey, value) => {
    if (compKey === "tshirt_given" && value && tshirtStock.length) {
      const row = rows.find((r) => r.id === rowId);
      return new Promise((resolve) => setTshirtAsk({ rowId, name: row?.client_name, resolve }));
    }
    return sendCompliance(rowId, { [compKey]: value });
  };

  const sendCompliance = async (rowId, payload) => {
    const prevRow = rows.find((r) => r.id === rowId);
    const prev = prevRow?.compliance;
    setRows((rs) => (rs || []).map((r) => (r.id === rowId ? { ...r, compliance: { ...(r.compliance || {}), ...payload } } : r)));
    try {
      const res = await updateAccountabilityCompliance(rowId, payload);
      setRows((rs) => (rs || []).map((r) => (r.id === rowId ? { ...r, compliance: res.compliance } : r)));
      if ("tshirt_given" in payload || "tshirt_out_of_stock" in payload) loadTshirtStock();
      return true;
    } catch (e) {
      setRows((rs) => (rs || []).map((r) => (r.id === rowId ? { ...r, compliance: prev } : r)));
      if (e?.response?.status === 403) {
        toast.error(e?.response?.data?.detail || "Only the assigned coach or member manager can update compliance");
      } else {
        toast.error(e?.response?.data?.detail || "Save failed");
      }
      return false;
    }
  };

  const runBackfill = () =>
    backfillAccountability()
      .then((r) => toast.success(`Backfilled ${r.created} trackers (of ${r.total_started_leads} started leads)`))
      .then(load)
      .catch((e) => toast.error(e?.response?.data?.detail || "Backfill failed"));

  return (
    <div className="space-y-8" data-testid="accountability-page">
      <div>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="eyebrow">Front End Challenge · FE Client Journey</div>
            <h2 className="font-display text-3xl font-extrabold mt-1 tracking-tight">Accountability</h2>
            <p className="text-sm text-zinc-400 mt-1 md:hidden">Tap a client to update their journey. Changes save as you go.</p>
            <p className="text-sm text-amber-200/90 mt-1 max-w-2xl" data-testid="nutri-start-explainer">
              The programme starts the day the nutrition consult is done. Mark &ldquo;Nutri consult&rdquo; Done on that day: it sets the sales and nutrition incentives.
            </p>
            <p className="text-sm text-zinc-400 mt-1 max-w-2xl hidden md:block">
              Most of the tracker fills itself in from the member app and gym data (cells marked <Zap className="inline h-3 w-3 text-sky-300" />).
              What&apos;s left to do shows up below, coach by coach. Click any other cell to edit; it saves as you go.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
          <Link to="/improvement?tab=coach" data-testid="coach-practice-link">
            <Button variant="outline" size="sm" className="border-[#262626] bg-transparent hover:bg-[#141414]">Coach practice</Button>
          </Link>
          {atLeast(user?.role, "manager") && (
            <Button variant="outline" size="sm" onClick={runBackfill} className="border-[#262626] bg-transparent hover:bg-[#141414]" data-testid="backfill-btn">
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Backfill from started leads
            </Button>
          )}
          </div>
        </div>
      </div>

      <NeedsAttention onOpenClient={openClient} reloadKey={attentionKey} />

      {/* Coaches: one score, and how their clients convert */}
      <section data-testid="coach-leaderboard">
        <button data-testid="accountability-button-240" onClick={() => setShowLeaderboard((v) => !v)} className="w-full flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Trophy className="h-4 w-4 text-amber-400" />
            <div className="eyebrow">Coaches</div>
          </div>
          {showLeaderboard ? <ChevronUp className="h-4 w-4 text-zinc-500" /> : <ChevronDown className="h-4 w-4 text-zinc-500" />}
        </button>
        {showLeaderboard && <CoachScoreboard stats={stats} user={user} />}
      </section>

      {/* Reports */}
      <section data-testid="accountability-reports">
        <button data-testid="accountability-button-309" onClick={() => setShowReports((v) => !v)} className="w-full flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-400" />
            <div className="eyebrow">Reports & flags</div>
          </div>
          {showReports ? <ChevronUp className="h-4 w-4 text-zinc-500" /> : <ChevronDown className="h-4 w-4 text-zinc-500" />}
        </button>
        {showReports && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <ReportCard icon={MessagesSquare} tint="amber" label="Feedback form gaps" count={reports.feedback_gaps.length} description="Clients with pending feedback forms" testid="report-feedback">
              {reports.feedback_gaps.slice(0, 4).map((r) => (
                <li key={r.id} className="text-[11px] text-zinc-400"><span className="text-zinc-200">{r.client_name}</span> <span className="text-zinc-500">· {r.coach}</span> <span className="text-amber-300/80">({r.missing.join(", ")})</span></li>
              ))}
              {reports.feedback_gaps.length > 4 && <li className="text-[10px] text-zinc-500">+{reports.feedback_gaps.length - 4} more</li>}
            </ReportCard>
            <ReportCard icon={Star} tint="rose" label="No Google review" count={reports.google_review_gaps.length} description="Finished clients without a Google review" testid="report-google">
              {reports.google_review_gaps.slice(0, 4).map((r) => (
                <li key={r.id} className="text-[11px] text-zinc-400"><span className="text-zinc-200">{r.client_name}</span> <span className="text-zinc-500">· {r.coach}</span></li>
              ))}
              {reports.google_review_gaps.length > 4 && <li className="text-[10px] text-zinc-500">+{reports.google_review_gaps.length - 4} more</li>}
            </ReportCard>
            <ReportCard icon={Sparkles} tint="rose" label="Coaches below 85" count={lowScore.length} description="Coach score under 85 this month" testid="report-lowscore">
              {lowScore.slice(0, 4).map((r) => (
                <li key={r.coach} className="text-[11px] text-zinc-400"><span className="text-zinc-200">{r.coach}</span> <span className="text-rose-300/80">· {r.score}</span></li>
              ))}
              {lowScore.length > 4 && <li className="text-[10px] text-zinc-500">+{lowScore.length - 4} more</li>}
            </ReportCard>
            {!noBloodTests && <ReportCard icon={Droplet} tint="rose" label="Blood tests missing" count={reports.bt_not_scheduled.length} description="Sale had blood test = Yes but dates not scheduled" testid="report-bloodtest">
              {reports.bt_not_scheduled.slice(0, 4).map((r) => (
                <li key={r.id} className="text-[11px] text-zinc-400"><span className="text-zinc-200">{r.client_name}</span> <span className="text-zinc-500">· {r.coach}</span> <span className="text-rose-300/80">({r.missing.join(" + ")})</span></li>
              ))}
              {reports.bt_not_scheduled.length > 4 && <li className="text-[10px] text-zinc-500">+{reports.bt_not_scheduled.length - 4} more</li>}
            </ReportCard>}
          </div>
        )}
      </section>

      {/* Filters */}
      <section>
        <div className="flex items-center justify-between mb-3 gap-2">
          <div className="flex items-center gap-2">
            <Filter className="h-3.5 w-3.5 text-zinc-500" />
            <div className="eyebrow">Client roster</div>
          </div>
          <div className="hidden md:block"><ColumnPicker hidden={hiddenCols} setHidden={setHiddenCols} noBloodTests={noBloodTests} /></div>
          <button type="button" onClick={() => setShowFilters((v) => !v)} aria-expanded={showFilters}
            className="md:hidden inline-flex items-center gap-1.5 rounded-full border border-[#262626] px-3 py-1.5 text-xs text-zinc-300">
            Filters{filters.coach !== "all" || filters.week_bucket !== "all" || filters.status !== "all" || endMonth !== "all" ? " · on" : ""}
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showFilters ? "rotate-180" : ""}`} />
          </button>
        </div>
        <div className={`card-surface p-4 mb-4 ${showFilters ? "" : "max-md:hidden"}`}>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Batch</Label>
              <Select data-testid="accountability-select-359" value={filters.week_bucket} onValueChange={(v) => setFilters((f) => ({ ...f, week_bucket: v }))}>
                <SelectTrigger className="bg-[#0a0a0a] border-[#262626] mt-1" data-testid="filter-week"><SelectValue placeholder="All batches" /></SelectTrigger>
                <SelectContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100">
                  <SelectItem data-testid="accountability-select-item-362" value="all">All batches</SelectItem>
                  {buckets.map((b) => (
                    <SelectItem data-testid="accountability-select-item-363" key={b.week_bucket} value={b.week_bucket}>
                      {b.start_date ? `Starts ${shortDate(b.start_date)} · ends ${shortDate(b.end_date)}` : b.week_bucket}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Accountability coach</Label>
              <div className="mt-1">
                <SearchableSelect data-testid="accountability-searchable-select-370"
                  value={filters.coach === "all" ? "" : filters.coach}
                  onChange={(v) => setFilters((f) => ({ ...f, coach: v || "all" }))}
                  options={[{ value: "__unassigned__", label: "Unassigned (no coach yet)" }, ...coaches.map((c) => ({ value: c.name, label: c.name }))]}
                  placeholder="All coaches"
                  emptyLabel="All coaches"
                  testid="filter-coach"
                />
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Status</Label>
              <Select data-testid="accountability-select-382" value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}>
                <SelectTrigger className="bg-[#0a0a0a] border-[#262626] mt-1" data-testid="filter-status"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100">
                  <SelectItem data-testid="accountability-select-item-385" value="all">All statuses</SelectItem>
                  <SelectItem data-testid="accountability-select-item-386" value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem data-testid="accountability-select-item-387" value="dropout">Dropout</SelectItem>
                  <SelectItem data-testid="accountability-select-item-388" value="extension">Extension</SelectItem>
                  <SelectItem data-testid="accountability-select-item-389" value="converted">Converted</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Ends in</Label>
              <Select value={endMonth} onValueChange={setEndMonth}>
                <SelectTrigger className="bg-[#0a0a0a] border-[#262626] mt-1" data-testid="filter-end-month"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100">
                  <SelectItem value="all">All months</SelectItem>
                  {endMonths.map((m) => (
                    <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <div className="text-xs text-zinc-500"><span className="text-zinc-200 font-medium tabular-nums">{shownRows.length}</span> clients match</div>
            </div>
          </div>
        </div>

        <NotStartedSection onMoved={load} />

        <div className="relative mb-3 md:max-w-sm">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search client, phone or coach…"
            className="pl-9 bg-[#0a0a0a] border-[#262626]" data-testid="acc-search" />
        </div>
        <div className="hidden md:block">
          <ExcelGrid rows={shownRows} patchCell={patchCell} patchCompliance={patchCompliance} hiddenCols={hiddenCols}
            people={people} noBloodTests={noBloodTests} />
        </div>
        <div className="md:hidden">
          <MobileRoster rows={shownRows} patchCell={patchCell} patchCompliance={patchCompliance} people={people} noBloodTests={noBloodTests} />
        </div>
      </section>
      {nutriAsk && (
        <NutriDoneDialog ask={nutriAsk} today={todayLocal(activeFacility?.timezone)}
          onSave={async (day) => {
            const ok = await sendNutri(nutriAsk.rowId, { nutrition_consult_booked: "Done", nutrition_consult_done_on: day });
            if (ok) { nutriAsk.resolve(true); setNutriAsk(null); }
          }}
          onCancel={() => { nutriAsk.resolve(false); setNutriAsk(null); }} />
      )}
      <Dialog open={!!openRow} onOpenChange={(o) => { if (!o) { setOpenRowId(null); setAttentionKey((k) => k + 1); } }}>
        {openRow && <ClientSheet row={openRow} patchCell={patchCell} patchCompliance={patchCompliance} people={people} noBloodTests={noBloodTests} />}
      </Dialog>
      {tshirtAsk && (
        <TshirtSizeDialog ask={tshirtAsk} sizes={tshirtStock}
          onPick={async (payload) => {
            const ok = await sendCompliance(tshirtAsk.rowId, payload);
            if (ok) { tshirtAsk.resolve(true); setTshirtAsk(null); }
          }}
          onCancel={() => { tshirtAsk.resolve(false); setTshirtAsk(null); }} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Phones: client cards → full-screen tracker for one client
// ---------------------------------------------------------------------------

const DONE_WORDS = new Set(["done", "yes", "filled"]);

// Sold members who haven't started (no start date yet) wait here, collapsed by default.
// Setting the start date moves them onto the tracker below (CR-0009).
function NotStartedSection({ onMoved }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const reload = () => listAccountabilityNotStarted().then(setItems).catch(() => setItems([]));
  useEffect(() => { reload(); }, []);
  const setStart = async (lead, date) => {
    if (!date) return;
    setBusy(lead.id);
    try {
      await updateLead(lead.id, { start_date: date });
      toast.success(`${lead.name} moved onto the tracker`);
      await reload();
      onMoved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save the start date");
    } finally {
      setBusy(null);
    }
  };
  if (!items.length) return null;
  const fmt = (d) => (d ? new Date(d).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : "—");
  return (
    <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.04] mb-3" data-testid="not-started-section">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-2 px-4 py-3 text-left"
        aria-expanded={open} data-testid="not-started-toggle">
        {open ? <ChevronDown className="h-4 w-4 text-amber-300" /> : <ChevronRight className="h-4 w-4 text-amber-300" />}
        <span className="font-semibold text-amber-200">Not started yet</span>
        <span className="rounded-full bg-amber-500/20 px-2 py-0.5 text-xs font-semibold text-amber-200 tabular-nums">{items.length}</span>
        <span className="text-xs text-zinc-500 ml-2 hidden sm:inline">Sold, no start date. Set the start date to move them onto the tracker.</span>
      </button>
      {open && (
        <div className="divide-y divide-[#1f1f1f] border-t border-amber-500/20">
          {items.map((l) => (
            <div key={l.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2.5" data-testid={`not-started-${l.id}`}>
              <div className="min-w-[10rem] flex-1">
                <div className="font-medium text-sm">{l.name}</div>
                <div className="text-[11px] text-zinc-500">{l.phone || "—"}</div>
              </div>
              <div className="text-xs text-zinc-400 w-40">{l.programme || "—"}</div>
              <div className="text-xs text-zinc-400 w-28">Sold {fmt(l.date)}</div>
              <div className="text-xs text-zinc-400 w-32 truncate">{l.sales_ex || "—"}</div>
              {l.lead_status === "sold_pending_payment" && <span className="text-[11px] text-amber-300">Payment pending</span>}
              <div className="flex items-center gap-2">
                <span className="text-xs text-zinc-500">Start date</span>
                <div className="w-36">
                  <DatePicker value={l.start_date || ""} disabled={busy === l.id}
                    onChange={(e) => setStart(l, e.target.value)} data-testid={`not-started-date-${l.id}`} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// "M" when a T-shirt was given from stock, "Out of stock" when marked not given for that reason.
function tshirtNote(compKey, compliance) {
  if (compKey !== "tshirt_given" || !compliance) return "";
  if (compliance.tshirt_given) return compliance.tshirt_size ? `Size ${compliance.tshirt_size}` : "";
  return compliance.tshirt_out_of_stock ? "Not given – out of stock" : "";
}

function NutriDoneDialog({ ask, today, onSave, onCancel }) {
  const [day, setDay] = useState(today);
  const [busy, setBusy] = useState(false);
  const bad = !day || day > today;
  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="bg-[#0f0f0f] border-[#262626] max-w-sm" data-testid="nutri-done-dialog">
        <DialogHeader><DialogTitle>Nutrition consult done{ask.name ? ` · ${ask.name}` : ""}</DialogTitle></DialogHeader>
        <p className="text-sm text-zinc-400">The programme starts the day the nutrition consult is done. That day sets the sales and nutrition incentives.</p>
        <div>
          <Label className="text-xs text-zinc-400">Done on which day?</Label>
          <Input type="date" value={day} max={today} onChange={(e) => setDay(e.target.value)}
            className="mt-1 bg-[#0a0a0a] border-[#262626]" data-testid="nutri-done-day" />
          {day > today && <div className="text-[11px] text-rose-300 mt-1">Not a future day.</div>}
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button type="button" disabled={bad || busy} data-testid="nutri-done-save"
            onClick={async () => { setBusy(true); await onSave(day); setBusy(false); }}>
            {busy ? "Saving" : "Mark done"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TshirtSizeDialog({ ask, sizes, onPick, onCancel }) {
  const [busy, setBusy] = useState(false);
  const pick = async (payload) => { setBusy(true); await onPick(payload); setBusy(false); };
  const sized = sizes.some((s) => s.variant);
  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="bg-[#0f0f0f] border-[#262626] max-w-sm" data-testid="tshirt-size-dialog">
        <DialogHeader><DialogTitle>T-shirt given{ask.name ? ` · ${ask.name}` : ""}</DialogTitle></DialogHeader>
        <p className="text-sm text-zinc-400">{sized ? "Which size? One is taken off this gym's stock." : "One is taken off this gym's stock."}</p>
        <div className="grid grid-cols-3 gap-2">
          {sizes.map((s) => (
            <Button key={s.id} type="button" variant="outline" disabled={busy || s.on_hand <= 0}
              onClick={() => pick({ tshirt_given: true, tshirt_size: s.variant || undefined })}
              className="h-auto flex-col py-2 border-[#262626]" data-testid={`tshirt-size-${s.variant || "one"}`}>
              <span className="text-base font-semibold">{s.variant || "Give one"}</span>
              <span className={`text-[11px] ${s.on_hand <= 0 ? "text-rose-400" : "text-zinc-500"}`}>{s.on_hand <= 0 ? "out of stock" : `${s.on_hand} left`}</span>
            </Button>
          ))}
        </div>
        <Button type="button" variant="ghost" disabled={busy} onClick={() => pick({ tshirt_out_of_stock: true })}
          className="w-full border border-amber-500/30 text-amber-300 hover:bg-amber-500/10" data-testid="tshirt-out-of-stock-btn">
          Not given – out of stock
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function trackerProgress(row) {
  // How much of the journey is ticked off: select fields with a "done"-type answer + compliance ticks.
  const sel = FLAT_COLS.filter((c) => c.type === "sel" && !["goal", "programme_status", "chat_status"].includes(c.key));
  const done = sel.filter((c) => DONE_WORDS.has(String(row[c.key] || "").toLowerCase())).length
    + COMPLIANCE_ITEMS.filter((c) => row.compliance?.[c.key]).length;
  return { done, total: sel.length + COMPLIANCE_ITEMS.length };
}

function MobileRoster({ rows, patchCell, patchCompliance, people, noBloodTests }) {
  const [openId, setOpenId] = useState(null);
  const shown = rows || [];
  const open = (rows || []).find((r) => r.id === openId);
  return (
    <div className="space-y-2" data-testid="acc-mobile-roster">
      {shown.map((r) => {
        const p = trackerProgress(r);
        return (
          <button key={r.id} type="button" onClick={() => setOpenId(r.id)} className="card-surface w-full text-left p-3.5" data-testid={`acc-card-${r.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-medium truncate">{r.client_name}</div>
                <div className="text-xs text-zinc-500 truncate">{[r.coach, r.program].filter(Boolean).join(" · ") || "—"}</div>
                {r.start_date && <div className="text-[11px] text-zinc-500">{shortDate(r.start_date)} → {shortDate(r.end_date)}</div>}
              </div>
              <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] ${badge(r.programme_status)}`}>{r.programme_status || "—"}</span>
            </div>
            <div className="mt-2.5 flex items-center gap-3">
              <div className="flex-1 h-1.5 rounded bg-[#1a1a1a] overflow-hidden">
                <div className="h-full bg-emerald-500" style={{ width: `${p.total ? (p.done / p.total) * 100 : 0}%` }} />
              </div>
              <span className="text-[11px] text-zinc-500 tabular-nums">{p.done}/{p.total} done</span>
              <span className={`text-[11px] font-mono ${r.coach_score >= 8.5 ? "text-emerald-300" : "text-amber-300"}`}>{r.coach_score ?? "–"}</span>
            </div>
          </button>
        );
      })}
      {shown.length === 0 && <div className="card-surface p-8 text-center text-sm text-zinc-500">No clients match.</div>}
      <Dialog open={!!open} onOpenChange={(v) => !v && setOpenId(null)}>
        {open && <ClientSheet row={open} patchCell={patchCell} patchCompliance={patchCompliance} people={people} noBloodTests={noBloodTests} />}
      </Dialog>
    </div>
  );
}

function ClientSheet({ row, patchCell, patchCompliance, people, noBloodTests }) {
  const [saveState, setSaveState] = useState(null);
  const [openGroups, setOpenGroups] = useState(() => new Set(["Basics"]));
  const track = async (p) => {
    setSaveState("saving");
    const ok = await p;
    setSaveState(ok ? "ok" : "err");
    if (ok) setTimeout(() => setSaveState(null), 1200);
  };
  const save = (key, value) => track(patchCell(row.id, key, value));
  const toggle = (g) => setOpenGroups((s) => { const n = new Set(s); n.has(g) ? n.delete(g) : n.add(g); return n; });

  const field = (c) => {
    const value = row[c.key];
    if (c.type === "check") {
      const checked = !!row.compliance?.[c.comp];
      return (
        <button key={c.key} type="button" onClick={() => track(patchCompliance(row.id, c.comp, !checked))}
          className={`w-full flex items-center gap-3 rounded-lg border px-3 py-3 text-sm text-left ${checked ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200" : "border-[#262626] text-zinc-300"}`}
          aria-pressed={checked}>
          <span className={`h-5 w-5 shrink-0 rounded border flex items-center justify-center ${checked ? "border-emerald-500 bg-emerald-500 text-black" : "border-zinc-600"}`}>{checked ? "✓" : ""}</span>
          {c.label}{tshirtNote(c.comp, row.compliance) && <span className="ml-auto text-xs text-zinc-400">{tshirtNote(c.comp, row.compliance)}</span>}
        </button>
      );
    }
    if (c.type === "ro") return null;
    const label = <div className="text-[11px] uppercase tracking-[0.14em] text-zinc-500 mb-1.5">{c.label}</div>;
    if ((row.auto_fields || []).includes(c.key)) {
      return (
        <div key={c.key}>{label}
          <div className="text-sm text-zinc-200 inline-flex items-center gap-1.5"><Zap className="h-3.5 w-3.5 text-sky-300" />{autoText(value)}
            <span className="text-xs text-zinc-500">filled in automatically</span></div>
        </div>
      );
    }
    if (c.type === "rodate") return <div key={c.key}>{label}<div className="text-sm text-zinc-300">{shortDate(value)}</div></div>;
    if (c.type === "rotext") return <div key={c.key}>{label}<div className="text-sm text-zinc-300" data-testid={`sheet-${c.key}`}>{c.fmt(value) || "—"}</div></div>;
    if (BT_DETAIL_KEYS.has(c.key) && !row.blood_test_required) return null;
    if (c.type === "person") {
      return (
        <div key={c.key}>
          {label}
          <PersonSelect value={value} options={people?.[c.roster] || []} onChange={(v) => save(c.key, v)} big />
        </div>
      );
    }
    if (c.type === "sel" || c.type === "bool") {
      const choices = c.type === "bool" ? [["no", "No"], ["yes", "Yes"]] : (c.choices || []).filter(Boolean).map((o) => [o, o]);
      const current = c.type === "bool" ? (value ? "yes" : "no") : (value ?? "");
      return (
        <div key={c.key}>
          {label}
          {c.key === "nutrition_consult_booked" && (
            <div className="text-[11px] text-zinc-500 -mt-1 mb-1.5" data-testid="sheet-nutri-note">
              {String(value || "").toLowerCase() === "done" && row.nutrition_consult_done_on
                ? `Done ${shortDate(row.nutrition_consult_done_on)}: the programme started that day.`
                : "The programme starts the day this is done."}
            </div>
          )}
          <div className="flex flex-wrap gap-1.5">
            {choices.map(([v, l]) => (
              <button key={v} type="button" aria-pressed={current === v}
                onClick={() => save(c.key, c.type === "bool" ? v === "yes" : (current === v ? null : v))}
                className={`rounded-full border px-3 py-1.5 text-sm ${current === v ? badge(v) + " font-medium" : "border-[#262626] text-zinc-400"}`}>
                {l}
              </button>
            ))}
          </div>
        </div>
      );
    }
    return (
      <div key={c.key}>
        {label}
        <SheetInput type={c.type === "num" ? "number" : c.type === "date" ? "date" : "text"} value={c.type === "date" ? (value || "").slice(0, 10) : (value ?? "")}
          onCommit={(v) => {
            if (c.type === "num") {
              if (v === "") return save(c.key, null);
              const n = Number(v);
              if (!Number.isNaN(n)) save(c.key, n);
              return undefined;
            }
            return save(c.key, v || (c.type === "date" ? null : ""));
          }} />
      </div>
    );
  };

  return (
    <DialogContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="acc-client-sheet">
      <DialogHeader className="text-left">
        <DialogTitle className="font-display text-xl pr-8">{row.client_name}</DialogTitle>
        <div className="text-xs text-zinc-500 flex items-center gap-2">
          {[row.phone, row.week_bucket].filter(Boolean).join(" · ")}
          {saveState === "saving" && <span className="inline-flex items-center gap-1 text-blue-300"><Loader2 className="h-3 w-3 animate-spin" />Saving</span>}
          {saveState === "ok" && <span className="inline-flex items-center gap-1 text-emerald-300"><CheckCircle2 className="h-3 w-3" />Saved</span>}
          {saveState === "err" && <span className="inline-flex items-center gap-1 text-rose-300"><XCircle className="h-3 w-3" />Not saved</span>}
        </div>
      </DialogHeader>
      <div className="space-y-2">
        {groupsFor(noBloodTests).filter((g) => g.group !== "Client").map((g) => {
          const isOpen = openGroups.has(g.group);
          const fields = g.cols.filter((c) => c.type !== "ro");
          if (!fields.length) return null;
          return (
            <section key={g.group} className="rounded-lg border border-[#1f1f1f]">
              <button type="button" onClick={() => toggle(g.group)} aria-expanded={isOpen}
                className="w-full flex items-center justify-between px-3 py-3 text-left">
                <span className="text-sm font-medium">{g.group}</span>
                <ChevronDown className={`h-4 w-4 text-zinc-500 transition-transform ${isOpen ? "" : "-rotate-90"}`} />
              </button>
              {isOpen && <div className="px-3 pb-3 space-y-3">{fields.map(field)}</div>}
            </section>
          );
        })}
        <section className="rounded-lg border border-[#1f1f1f]">
          <button type="button" onClick={() => toggle("fit3d")} aria-expanded={openGroups.has("fit3d")}
            className="w-full flex items-center justify-between px-3 py-3 text-left">
            <span className="text-sm font-medium">Fit3D scans</span>
            <ChevronDown className={`h-4 w-4 text-zinc-500 transition-transform ${openGroups.has("fit3d") ? "" : "-rotate-90"}`} />
          </button>
          {openGroups.has("fit3d") && <div className="px-3 pb-3"><Fit3DSubTable row={row} onSave={save} /></div>}
        </section>
      </div>
    </DialogContent>
  );
}

// Commits on blur / Enter, like the grid's CellInput, but sized for thumbs.
function SheetInput({ value, onCommit, type = "text" }) {
  const [local, setLocal] = useState(value ?? "");
  const [focused, setFocused] = useState(false);
  useEffect(() => { if (!focused) setLocal(value ?? ""); }, [value, focused]);
  return (
    <Input type={type} value={local} inputMode={type === "number" ? "decimal" : undefined}
      onFocus={() => setFocused(true)}
      onBlur={() => { setFocused(false); if (String(local) !== String(value ?? "")) onCommit(local); }}
      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
      onChange={(e) => setLocal(e.target.value)}
      className={`bg-[#0a0a0a] border-[#262626] ${type === "date" ? "[color-scheme:dark]" : ""}`} />
  );
}

// ---------------------------------------------------------------------------
// Excel-style inline-editing grid
// ---------------------------------------------------------------------------

// Sticky offsets — the grid has its OWN scroll viewport (see .acc-grid-scroll in
// App.css) so headers resolve against it, not the window. `top: 0` pins to the top
// of the grid scrollport; the column-header row sits directly under the group row.
const HEADER_TOP_PX = 0;
const GROUP_ROW_HEIGHT_PX = 26;

// Column-picker persistence. Keyed by facility so different gyms remember different views.
const HIDABLE_COL_KEYS = (FLAT_COLS || [])
  .filter((c) => !c.sticky && !c.key.startsWith("__"))
  .map((c) => c.key);

function loadHiddenCols(facilityId) {
  try {
    const raw = localStorage.getItem(`acc-grid-hidden-cols:${facilityId || "default"}`);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch { return new Set(); }
}

function saveHiddenCols(facilityId, hidden) {
  try {
    localStorage.setItem(`acc-grid-hidden-cols:${facilityId || "default"}`, JSON.stringify([...hidden]));
  } catch { /* localStorage quota or disabled — silently no-op */ }
}

function ExcelGrid({ rows, patchCell, patchCompliance, hiddenCols, people, noBloodTests }) {
  const [expanded, setExpanded] = useState({}); // rowId -> bool (Fit3D sub-row)
  const scrollRef = useRef(null);

  const toggleExpand = (id) => setExpanded((s) => ({ ...s, [id]: !s[id] }));

  // Recompute visible column groups and flat list whenever the hidden set changes so
  // grid stays perfectly aligned (colSpan on the group header row must match live cols).
  const visibleGroups = useMemo(() => {
    return groupsFor(noBloodTests)
      .map((g) => ({ ...g, cols: (g.cols || []).filter((c) => c.sticky || !hiddenCols.has(c.key)) }))
      .filter((g) => g.cols.length > 0);
  }, [hiddenCols, noBloodTests]);
  const visibleFlatCols = useMemo(() => (visibleGroups || []).flatMap((g) => g.cols), [visibleGroups]);
  const totalCols = visibleFlatCols.length;

  return (
    <div className="card-surface overflow-hidden" data-testid="acc-grid">
      {/* The grid has its OWN bounded scroll viewport (see .acc-grid-scroll in App.css) so
          sticky <thead> pins reliably in Chromium and matches Excel's freeze-panes feel. */}
      <div className="acc-grid-scroll" ref={scrollRef} id="acc-grid-scrollport">
        <table className="text-xs border-collapse" style={{ minWidth: "100%" }}>
          <thead>
            {/* Group header row — sticky vertically so the section context survives long lists. */}
            <tr className="bg-[#0d0d0d] border-b border-[#1f1f1f]">
              {(visibleGroups || []).map((g) => (
                <th key={g.group} colSpan={g.cols.length}
                  style={{ top: HEADER_TOP_PX }}
                  className={`sticky px-2 py-1.5 text-[9px] uppercase tracking-[0.18em] text-zinc-500 border-r border-[#1a1a1a] text-left bg-[#0d0d0d] ${g.group === "Client" ? "left-0 z-40" : "z-30"}`}>
                  {g.group}
                </th>
              ))}
            </tr>
            {/* Column header row — pinned directly under the group row. */}
            <tr className="bg-[#0d0d0d] border-b border-[#1f1f1f]">
              {(visibleFlatCols || []).map((c) => (
                <th key={c.key}
                  style={{ minWidth: c.width, top: HEADER_TOP_PX + GROUP_ROW_HEIGHT_PX }}
                  className={`sticky px-2 py-2 text-[9px] uppercase tracking-[0.12em] text-zinc-500 font-medium text-left whitespace-nowrap border-r border-[#141414] bg-[#0d0d0d] ${c.sticky ? "left-0 z-40" : "z-30"}`}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={totalCols} className="px-6 py-12 text-center text-sm text-zinc-500">
                No clients in this view yet. Trackers appear automatically on the <span className="text-zinc-300">programme start date</span> of a sold Front End consult.
              </td></tr>
            )}
            {(rows || []).map((r) => (
              <GridRow key={r.id} row={r} patchCell={patchCell} patchCompliance={patchCompliance} people={people}
                expanded={!!expanded[r.id]} onToggleExpand={() => toggleExpand(r.id)}
                visibleGroups={visibleGroups} visibleFlatCols={visibleFlatCols} totalCols={totalCols} />
            ))}
          </tbody>
        </table>
      </div>
      <GridScrollbar targetRef={scrollRef} />
    </div>
  );
}

// Always-visible custom horizontal scrollbar for the accountability grid. Chromium's
// overlay scrollbar behavior ignores ::-webkit-scrollbar sizing on several platforms,
// so we render our own track + draggable thumb and keep it pinned under the table.
// The native scroller is visually hidden but still functional — wheel/shift-wheel and
// touchpad swipes keep working, this bar just gives users a guaranteed visible control.
function GridScrollbar({ targetRef }) {
  const trackRef = useRef(null);
  const drag = useRef(null); // { startX, startScrollLeft }
  const [thumb, setThumb] = useState({ left: 0, width: 100 });

  const recalc = useCallback(() => {
    const el = targetRef.current;
    const track = trackRef.current;
    if (!el || !track) return;
    const { scrollWidth, clientWidth, scrollLeft } = el;
    if (scrollWidth <= clientWidth) {
      setThumb({ left: 0, width: 100 });
      return;
    }
    const trackW = track.clientWidth;
    const width = Math.max((clientWidth / scrollWidth) * trackW, 24);
    const maxLeft = trackW - width;
    const left = (scrollLeft / (scrollWidth - clientWidth)) * maxLeft;
    setThumb({ left, width });
  }, [targetRef]);

  useEffect(() => {
    const el = targetRef.current;
    if (!el) return undefined;
    recalc();
    const ro = new ResizeObserver(recalc);
    ro.observe(el);
    el.addEventListener("scroll", recalc, { passive: true });
    window.addEventListener("resize", recalc);
    return () => {
      ro.disconnect();
      el.removeEventListener("scroll", recalc);
      window.removeEventListener("resize", recalc);
    };
  }, [targetRef, recalc]);

  const onPointerDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const el = targetRef.current;
    if (!el) return;
    drag.current = { startX: e.clientX, startScrollLeft: el.scrollLeft };
    e.target.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e) => {
    const el = targetRef.current;
    const track = trackRef.current;
    if (!drag.current || !el || !track) return;
    const { scrollWidth, clientWidth } = el;
    if (scrollWidth <= clientWidth) return;
    const trackW = track.clientWidth;
    const maxLeft = trackW - thumb.width;
    if (maxLeft <= 0) return;
    const dx = e.clientX - drag.current.startX;
    const scrollDelta = (dx / maxLeft) * (scrollWidth - clientWidth);
    el.scrollLeft = drag.current.startScrollLeft + scrollDelta;
  };

  const onPointerUp = () => { drag.current = null; };

  // Click on the empty track (outside the thumb) pages the view left/right.
  const onTrackClick = (e) => {
    const el = targetRef.current;
    const track = trackRef.current;
    if (!el || !track) return;
    const rect = track.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const { scrollWidth, clientWidth } = el;
    if (scrollWidth <= clientWidth) return;
    const maxLeft = rect.width - thumb.width;
    if (maxLeft <= 0) return;
    const ratio = Math.min(Math.max((clickX - thumb.width / 2) / maxLeft, 0), 1);
    el.scrollLeft = ratio * (scrollWidth - clientWidth);
  };

  return (
    <div
      ref={trackRef}
      className="acc-grid-scrollbar"
      onPointerDown={(e) => { if (e.target === e.currentTarget) onTrackClick(e); }}
      data-testid="acc-scrollbar"
    >
      <div
        className="acc-grid-scrollbar-thumb"
        style={{ left: thumb.left, width: thumb.width }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        data-testid="acc-scrollbar-thumb"
        role="scrollbar"
        aria-controls="acc-grid-scrollport"
        aria-orientation="horizontal"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round((thumb.left / Math.max(1, (trackRef.current?.clientWidth || 1) - thumb.width)) * 100) || 0}
        aria-label="Scroll accountability grid horizontally"
        tabIndex={0}
      />
    </div>
  );
}

function GridRow({ row, patchCell, patchCompliance, people, expanded, onToggleExpand, visibleFlatCols, totalCols }) {
  const [saveState, setSaveState] = useState(null); // null | saving | ok | err

  // Wrap patchCell with a per-row save indicator so the team lead sees each cell persist.
  const save = async (key, value) => {
    setSaveState("saving");
    const ok = await patchCell(row.id, key, value);
    setSaveState(ok ? "ok" : "err");
    if (ok) setTimeout(() => setSaveState(null), 1500);
  };

  const saveCompliance = async (compKey, value) => {
    setSaveState("saving");
    const ok = await patchCompliance(row.id, compKey, value);
    setSaveState(ok ? "ok" : "err");
    if (ok) setTimeout(() => setSaveState(null), 1500);
  };

  const renderCell = (c) => {
    if (c.key === "__client") {
      return (
        <td key={c.key} className="px-2 py-1.5 sticky left-0 z-10 bg-[#0f0f0f] border-r border-[#1f1f1f]">
          <div className="flex items-center gap-1.5">
            <button onClick={onToggleExpand} className="text-zinc-500 hover:text-zinc-200" title="Weekly measurement checks"
              data-testid={`fit3d-toggle-${row.id}`}>
              {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            </button>
            <div>
              <div className="font-medium text-zinc-100 whitespace-nowrap">{row.client_name}</div>
              <div className="text-[10px] text-zinc-500">{row.phone || row.week_bucket || "—"}</div>
            </div>
            {saveState === "saving" && <Loader2 className="h-3 w-3 animate-spin text-blue-400" />}
            {saveState === "ok" && <CheckCircle2 className="h-3 w-3 text-emerald-400" />}
            {saveState === "err" && <XCircle className="h-3 w-3 text-rose-400" />}
          </div>
        </td>
      );
    }
    if (c.key === "__score") {
      return (
        <td key={c.key} className="px-2 py-1.5 border-r border-[#141414] text-center">
          <div className={`font-mono ${row.coach_score >= 8.5 ? "text-emerald-300" : "text-amber-300"}`}>{row.coach_score}</div>
          <div className="text-[9px] text-zinc-600">/{row.total_score}</div>
        </td>
      );
    }
    if (c.key === "__fit3d") {
      return (
        <td key={c.key} className="px-2 py-1.5 border-r border-[#141414] text-center text-zinc-500">
          <button onClick={onToggleExpand} className="text-[10px] text-blue-300 hover:text-blue-200" data-testid={`fit3d-expand-${row.id}`}>
            {expanded ? "hide" : "show"}
          </button>
        </td>
      );
    }
    if (c.type === "check") {
      const checked = !!row.compliance?.[c.comp];
      const note = tshirtNote(c.comp, row.compliance);
      return (
        <td key={c.key} className="px-1 py-1.5 border-r border-[#141414] text-center">
          <button
            onClick={() => { saveCompliance(c.comp, !checked); }}
            className={`inline-flex h-4.5 min-w-[1.125rem] px-0.5 items-center justify-center rounded border text-[10px] ${
              checked ? "border-emerald-500 bg-emerald-500/20 text-emerald-300"
                : note ? "border-amber-500/50 bg-amber-500/10 text-amber-300"
                  : "border-[#333] bg-[#0a0a0a] text-transparent hover:border-zinc-500"
            }`}
            title={note ? `${c.label}: ${note}` : c.label}
            data-testid={`cell-${c.comp}-${row.id}`}
          >
            {checked && row.compliance?.tshirt_size && c.comp === "tshirt_given" ? row.compliance.tshirt_size : note && !checked ? "–" : "✓"}
          </button>
        </td>
      );
    }
    const value = row[c.key];
    if (c.type === "rodate") {
      return <td key={c.key} className="px-2 py-1.5 border-r border-[#141414] text-[11px] text-zinc-300 whitespace-nowrap">{shortDate(value)}</td>;
    }
    if ((row.auto_fields || []).includes(c.key)) {
      return (
        <td key={c.key} className="px-2 py-1.5 border-r border-[#141414] text-[11px] whitespace-nowrap" title="Filled in automatically" data-testid={`cell-${c.key}-${row.id}`}>
          <span className={`inline-flex items-center gap-1 ${badgeText(autoText(value))}`}><Zap className="h-3 w-3 text-sky-300" />{autoText(value)}</span>
        </td>
      );
    }
    if (c.type === "rotext") {
      const text = c.fmt(value);
      return (
        <td key={c.key} className="px-2 py-1.5 border-r border-[#141414] text-[11px] text-zinc-300 whitespace-nowrap"
          title={text ? `${c.label}: ${text} (set on the consult)` : "Not set on the consult"} data-testid={`cell-${c.key}-${row.id}`}>
          {text || <span className="text-zinc-600">—</span>}
        </td>
      );
    }
    if (BT_DETAIL_KEYS.has(c.key) && !row.blood_test_required) {
      return <td key={c.key} className="px-2 py-1.5 border-r border-[#141414] text-[11px] text-zinc-600" title="No blood tests for this member">—</td>;
    }
    if (c.type === "person") {
      return (
        <td key={c.key} className="px-1 py-1 border-r border-[#141414]">
          <PersonSelect value={value} options={people?.[c.roster] || []} onChange={(v) => save(c.key, v)} testid={`cell-${c.key}-${row.id}`} />
        </td>
      );
    }
    if (c.type === "sel") {
      return (
        <td key={c.key} className="px-1 py-1 border-r border-[#141414]">
          <select
            value={value ?? ""}
            onChange={(e) => save(c.key, e.target.value || null)}
            className={`w-full bg-transparent text-[11px] px-1 py-1 rounded hover:bg-[#1a1a1a] focus:bg-[#1a1a1a] focus:outline-none ${badgeText(value)}`}
            data-testid={`cell-${c.key}-${row.id}`}
          >
            {(c.choices || []).map((o) => (<option key={o} value={o}>{o || "—"}</option>))}
          </select>
          {c.key === "nutrition_consult_booked" && row.nutrition_consult_done_on && String(value || "").toLowerCase() === "done" && (
            <div className="px-1 text-[10px] text-emerald-300/80 whitespace-nowrap" title="Programme started this day"
              data-testid={`nutri-done-on-${row.id}`}>{shortDate(row.nutrition_consult_done_on)}</div>
          )}
        </td>
      );
    }
    if (c.type === "bool") {
      return (
        <td key={c.key} className="px-1 py-1 border-r border-[#141414]">
          <select
            value={value ? "yes" : "no"}
            onChange={(e) => save(c.key, e.target.value === "yes")}
            className={`w-full bg-transparent text-[11px] px-1 py-1 rounded hover:bg-[#1a1a1a] focus:bg-[#1a1a1a] focus:outline-none ${value ? "text-emerald-300" : "text-zinc-400"}`}
            data-testid={`cell-${c.key}-${row.id}`}
          >
            <option value="no">No</option>
            <option value="yes">Yes</option>
          </select>
        </td>
      );
    }
    if (c.type === "num") {
      return (
        <td key={c.key} className="px-1 py-1 border-r border-[#141414]">
          <CellInput
            type="number" step="0.1"
            value={value ?? ""}
            onCommit={(v) => {
              if (v === "") return save(c.key, null);
              const n = Number(v);
              if (Number.isNaN(n)) return; // guard — never persist NaN from a malformed entry
              save(c.key, n);
            }}
            className="font-mono text-right"
            testid={`cell-${c.key}-${row.id}`}
          />
        </td>
      );
    }
    if (c.type === "date") {
      return (
        <td key={c.key} className="px-1 py-1 border-r border-[#141414]">
          <CellInput
            type="date"
            value={(value || "").slice(0, 10)}
            onCommit={(v) => save(c.key, v || null)}
            testid={`cell-${c.key}-${row.id}`}
          />
        </td>
      );
    }
    // text (default)
    return (
      <td key={c.key} className="px-1 py-1 border-r border-[#141414]">
        <CellInput
          value={value ?? ""}
          onCommit={(v) => save(c.key, v)}
          testid={`cell-${c.key}-${row.id}`}
        />
      </td>
    );
  };

  return (
    <>
      <tr className="border-b border-[#1a1a1a] hover:bg-[#121212]" data-testid={`acc-row-${row.id}`}>
        {(visibleFlatCols || []).map(renderCell)}
      </tr>
      {expanded && (
        <tr className="border-b border-[#1a1a1a] bg-[#0c0c0c]">
          <td colSpan={totalCols} className="px-4 py-3">
            <Fit3DSubTable row={row} onSave={save} />
          </td>
        </tr>
      )}
    </>
  );
}

// Coach / Nutrition coach picker (CR-0007): names from the gym's Team Roster, so one coach is
// always spelled one way. A name already on the row that isn't on the roster stays selectable.
function PersonSelect({ value, options, onChange, testid, big = false }) {
  const names = value && !options.includes(value) ? [value, ...options] : options;
  return (
    <select value={value || ""} onChange={(e) => onChange(e.target.value || null)} data-testid={testid}
      className={big
        ? "w-full rounded-md border border-[#262626] bg-[#0a0a0a] px-3 py-2 text-sm text-zinc-200"
        : `w-full bg-transparent text-[11px] px-1 py-1 rounded hover:bg-[#1a1a1a] focus:bg-[#1a1a1a] focus:outline-none ${value ? "text-zinc-200" : "text-zinc-500"}`}>
      <option value="">—</option>
      {names.map((n) => (<option key={n} value={n}>{n}{options.includes(n) ? "" : " (not on roster)"}</option>))}
    </select>
  );
}

// Colour the select text by value so Done/Yes pop green and Missed/No pop red at a glance.
function badgeText(val) {
  const v = (val || "").toString().toLowerCase();
  if (["done", "yes", "filled", "active"].includes(v)) return "text-emerald-300";
  if (["missed", "no", "dropout"].includes(v)) return "text-rose-300";
  if (["pending", "scheduled"].includes(v)) return "text-amber-300";
  if (["extension"].includes(v)) return "text-blue-300";
  return "text-zinc-400";
}

// Uncontrolled-per-edit input: holds local text while typing, commits on blur or Enter.
// This keeps typing snappy and avoids a server roundtrip per keystroke.
function CellInput({ value, onCommit, type = "text", step, className = "", testid }) {
  const [local, setLocal] = useState(value);
  const [focused, setFocused] = useState(false);
  useEffect(() => { if (!focused) setLocal(value ?? ""); }, [value, focused]);

  const commit = () => {
    const incoming = value ?? "";
    if (String(local) !== String(incoming)) onCommit(local);
  };

  return (
    <input
      type={type}
      step={step}
      value={local}
      onFocus={() => setFocused(true)}
      onBlur={() => { setFocused(false); commit(); }}
      onKeyDown={(e) => { if (e.key === "Enter") { e.target.blur(); } }}
      onChange={(e) => setLocal(e.target.value)}
      inputMode={type === "number" ? "decimal" : undefined}
      className={`w-full bg-transparent text-[11px] px-1 py-1 rounded hover:bg-[#1a1a1a] focus:bg-[#1a1a1a] focus:outline-none text-zinc-200 ${type === "date" ? "[color-scheme:dark]" : ""} ${type === "number" ? "[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" : ""} ${className}`}
      data-testid={testid}
    />
  );
}

// Inline Fit3D weekly-scan matrix — the only genuinely nested data on a tracker. It
// stays inside an expandable row so the main grid doesn't sprawl 24 more columns.
function Fit3DSubTable({ row, onSave }) {
  const scans = row.fit3d_scans || [];
  if (!scans.length) return <div className="text-xs text-zinc-500">No weekly checks set up for this client.</div>;
  const setScan = (idx, k, v) => {
    const next = scans.map((s, i) => i === idx ? { ...s, [k]: v } : s);
    onSave("fit3d_scans", next);
  };
  return (
    <div className="max-w-3xl" data-testid={`fit3d-subtable-${row.id}`}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Measurement checks · weekly (filled from the member app)</div>
      <table className="text-xs w-full">
        <thead>
          <tr className="text-left text-[9px] uppercase tracking-[0.15em] text-zinc-600 border-b border-[#1a1a1a]">
            <th className="py-1 pr-3">Wk</th><th className="py-1 pr-3">Date</th><th className="py-1 pr-3">Weight</th><th className="py-1 pr-3">BF %</th><th className="py-1 pr-3">Measurement</th><th className="py-1">Status</th>
          </tr>
        </thead>
        <tbody>
          {scans.map((s, i) => (
            <tr key={s.week ?? i} className="border-b border-[#141414] last:border-0" data-testid={`fit3d-row-${s.week}`}>
              <td className="py-1 pr-3 text-zinc-400">W{s.week}</td>
              <td className="py-1 pr-3"><CellInput type="date" value={(s.date || "").slice(0, 10)} onCommit={(v) => setScan(i, "date", v)} testid={`fit3d-date-${s.week}-${row.id}`} /></td>
              <td className="py-1 pr-3"><CellInput type="number" step="0.1" value={s.weight ?? ""} onCommit={(v) => setScan(i, "weight", v === "" ? null : Number(v))} className="font-mono" testid={`fit3d-weight-${s.week}-${row.id}`} /></td>
              <td className="py-1 pr-3"><CellInput type="number" step="0.1" value={s.bf_pct ?? ""} onCommit={(v) => setScan(i, "bf_pct", v === "" ? null : Number(v))} className="font-mono" testid={`fit3d-bf-${s.week}-${row.id}`} /></td>
              <td className="py-1 pr-3"><CellInput type="number" step="0.1" value={s.measurement ?? ""} onCommit={(v) => setScan(i, "measurement", v === "" ? null : Number(v))} className="font-mono" testid={`fit3d-meas-${s.week}-${row.id}`} /></td>
              <td className="py-1">
                <select value={s.status || "Pending"} onChange={(e) => setScan(i, "status", e.target.value)}
                  className={`bg-transparent text-[11px] px-1 py-0.5 rounded hover:bg-[#1a1a1a] focus:outline-none ${badgeText(s.status)}`}
                  data-testid={`fit3d-status-${s.week}-${row.id}`}>
                  {FIT3D_STATUS.map((v) => (<option key={v} value={v}>{v}</option>))}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ReportCard({ icon: Icon, tint, label, count, description, children, testid }) {
  const tintClasses = {
    amber: "border-amber-500/30 bg-amber-500/[0.03]",
    rose: "border-rose-500/30 bg-rose-500/[0.03]",
    emerald: "border-emerald-500/30 bg-emerald-500/[0.03]",
  }[tint] || "border-[#262626] bg-[#0d0d0d]";
  const iconTint = { amber: "text-amber-400", rose: "text-rose-400", emerald: "text-emerald-400" }[tint] || "text-zinc-400";
  return (
    <div className={`card-surface p-4 border ${tintClasses}`} data-testid={testid}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <Icon className={`h-4 w-4 ${iconTint}`} />
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">{label}</div>
          </div>
          <div className="mt-2 text-3xl font-bold text-zinc-100 tabular-nums">{count}</div>
          <div className="text-[11px] text-zinc-500 mt-0.5">{description}</div>
        </div>
      </div>
      {count > 0 && children && (
        <ul className="mt-3 space-y-1.5 pt-3 border-t border-[#1f1f1f]">{children}</ul>
      )}
    </div>
  );
}


// Column visibility picker — opens a popover with a checkbox per column, grouped
// under the same headers the grid uses. Persists to localStorage per facility.
function ColumnPicker({ hidden, setHidden, noBloodTests }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close on outside click. Small enough that a global escape hatch feels overkill.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const toggle = (key) => {
    setHidden((h) => {
      const next = new Set(h);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const showAll = () => setHidden(new Set());
  const compactView = () => {
    // Sensible "lean coach" preset — hide the two rarely-referenced blocks.
    const lean = new Set();
    for (const g of COLUMN_GROUPS) {
      if (["Feedback forms", "Post-sale compliance"].includes(g.group)) {
        for (const c of g.cols) if (!c.sticky) lean.add(c.key);
      }
    }
    setHidden(lean);
  };

  const visibleCount = HIDABLE_COL_KEYS.length - [...hidden].filter((k) => HIDABLE_COL_KEYS.includes(k)).length;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 text-[11px] text-zinc-300 hover:text-zinc-100 border border-[#262626] hover:border-[#3a3a3a] rounded-full px-3 py-1.5 bg-[#0a0a0a]"
        data-testid="acc-columns-btn"
      >
        <Columns3 className="h-3.5 w-3.5" />
        Columns
        <span className="text-[10px] text-zinc-500">{visibleCount}/{HIDABLE_COL_KEYS.length}</span>
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 z-50 w-80 max-h-[70vh] overflow-y-auto card-surface border border-[#262626] rounded-lg shadow-xl bg-[#0f0f0f]"
          data-testid="acc-columns-popover"
        >
          <div className="sticky top-0 bg-[#0f0f0f] border-b border-[#1f1f1f] px-4 py-2.5 flex items-center justify-between gap-2">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Visible columns</div>
            <div className="flex items-center gap-2">
              <button onClick={compactView} className="text-[10px] text-blue-300 hover:text-blue-200" data-testid="acc-columns-lean">Coach view</button>
              <button onClick={showAll} className="text-[10px] text-blue-300 hover:text-blue-200" data-testid="acc-columns-all">Show all</button>
            </div>
          </div>
          <div className="p-3 space-y-3">
            {groupsFor(noBloodTests).map((g) => {
              const groupCols = (g.cols || []).filter((c) => !c.sticky && !c.key.startsWith("__"));
              if (groupCols.length === 0) return null; // hides the sticky-only "Client" group
              const allHidden = (groupCols || []).every((c) => hidden.has(c.key));
              const toggleGroup = () => {
                setHidden((h) => {
                  const next = new Set(h);
                  if (allHidden) { for (const c of groupCols) next.delete(c.key); }
                  else { for (const c of groupCols) next.add(c.key); }
                  return next;
                });
              };
              return (
                <div key={g.group}>
                  <button
                    onClick={toggleGroup}
                    className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-zinc-400 hover:text-zinc-100 mb-1.5"
                    data-testid={`acc-columns-group-${g.group}`}
                  >
                    {allHidden ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                    {g.group}
                  </button>
                  <div className="grid grid-cols-2 gap-1 pl-1">
                    {(groupCols || []).map((c) => (
                      <label key={c.key} className="flex items-center gap-2 text-xs text-zinc-200 cursor-pointer hover:text-zinc-100 py-0.5">
                        <input
                          type="checkbox"
                          checked={!hidden.has(c.key)}
                          onChange={() => toggle(c.key)}
                          className="h-3.5 w-3.5 accent-blue-500"
                          data-testid={`acc-column-toggle-${c.key}`}
                        />
                        <span className="truncate">{c.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
